import IFormFieldItem from '@/module/design-system/components/models/FormComponent/IFormFieldItem';
import IVueInstance from '@/module/common/models/IVueInstance';
import EServiceContainerInstances from '@/module/common/service/ServiceContainer/EServiceContainerInstances';
import TValidatorsBlocks from '@/module/merchant/view/AccountActivatePage/Validator/TValidatorsBlocks';
import TValidatorsFields from '@/module/merchant/view/AccountActivatePage/Validator/TValidatorsFields';
import Validator from '@/module/common/utils/validator/Validator';
import EValidatorType from '@/module/common/utils/validator/enum/EValidatorType';
import IServiceContainer from '@/module/common/service/ServiceContainer/IServiceContainer';
import ILocalisationRegistry from '@/module/common/utils/localisation/ILocalisationRegistry';
import cloneImmutable from '@/module/common/utils/data/cloneImmutable';
import IFieldStatus from '@/module/design-system/components/models/FormComponent/IFieldStatus';
import EValidationFieldStatusCode from '@/module/common/components/FormComponent/EValidationFieldStatusCode';
import checkCondition from '@/module/common/helpers/checkCondition';
import IFieldValidatorStatusHandler
  from '@/module/merchant/view/AccountActivatePage/Validator/IFieldValidatorStatusHandler';
import ITabValidatorStatusHandler
  from '@/module/merchant/view/AccountActivatePage/Validator/ITabValidatorStatusHandler';
import EFieldName from '@/module/merchant/view/AccountActivatePage/models/EFieldName';
import EFieldBlock from '@/module/merchant/view/AccountActivatePage/models/EFieldBlock';
import EPageTab from '@/module/merchant/view/AccountActivatePage/models/EPageTab';
import ETabStatus from '@/module/merchant/view/AccountActivatePage/models/ETabStatus';
import IPageState from '@/module/merchant/view/AccountActivatePage/models/IPageState';
import IFormConfig from '@/module/merchant/view/AccountActivatePage/models/IFormConfig';
import TFormData from '@/module/merchant/view/AccountActivatePage/models/FormData/TFormData';
import IValidatorHandler from '@/module/merchant/view/AccountActivatePage/Validator/IValidatorHandler';
import TFieldStatuses from '@/module/merchant/view/AccountActivatePage/models/TFieldStatuses';

class ValidatorHandler implements IValidatorHandler {
  private readonly serviceContainer: IServiceContainer;

  private readonly localisationRegistry: ILocalisationRegistry;

  private readonly state: IPageState;

  private readonly fieldValidatorHandler: IFieldValidatorStatusHandler;

  private readonly tabValidatorHandler: ITabValidatorStatusHandler;

  constructor(
    serviceContainer: IServiceContainer,
    localisationRegistry: ILocalisationRegistry,
    state: IPageState,
    fieldValidatorHandler: IFieldValidatorStatusHandler,
    tabValidatorHandler: ITabValidatorStatusHandler,
  ) {
    this.localisationRegistry = localisationRegistry;
    this.serviceContainer = serviceContainer;
    this.state = state;
    this.fieldValidatorHandler = fieldValidatorHandler;
    this.tabValidatorHandler = tabValidatorHandler;
  }

  getValidators = (): TValidatorsBlocks => {
    const validationMessagesLocalisation = this.localisationRegistry.validationMessages;
    const vueInstance = this.serviceContainer.resolve<IVueInstance>(EServiceContainerInstances.VUE_INSTANCE);

    const businessStructure = EFieldBlock.BUSINESS_STRUCTURE;
    const businessRepresentative = EFieldBlock.BUSINESS_REPRESENTATIVE;
    const businessDetails = EFieldBlock.BUSINESS_DETAILS;
    const accountForPayouts = EFieldBlock.ACCOUNT_FOR_PAYOUTS;
    const supportDetails = EFieldBlock.SUPPORT_DETAILS;

    const validatorsRegistry = {} as TValidatorsBlocks;

    validatorsRegistry[businessStructure] = {} as TValidatorsFields;
    validatorsRegistry[businessRepresentative] = {} as TValidatorsFields;
    validatorsRegistry[businessDetails] = {} as TValidatorsFields;
    validatorsRegistry[accountForPayouts] = {} as TValidatorsFields;
    validatorsRegistry[supportDetails] = {} as TValidatorsFields;

    validatorsRegistry[businessStructure][EFieldName.COUNTRY] = new Validator();
    validatorsRegistry[businessStructure][EFieldName.COUNTRY].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );

    validatorsRegistry[businessStructure][EFieldName.ADDRESS_LINE_1] = new Validator();
    validatorsRegistry[businessStructure][EFieldName.ADDRESS_LINE_1].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessStructure][EFieldName.ADDRESS_LINE_1].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessStructure][EFieldName.ADDRESS_LINE_2] = new Validator();
    validatorsRegistry[businessStructure][EFieldName.ADDRESS_LINE_2].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessStructure][EFieldName.TOWN_OR_CITY] = new Validator();
    validatorsRegistry[businessStructure][EFieldName.TOWN_OR_CITY].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessStructure][EFieldName.TOWN_OR_CITY].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessStructure][EFieldName.POSTAL_CODE] = new Validator();
    validatorsRegistry[businessStructure][EFieldName.POSTAL_CODE].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessStructure][EFieldName.POSTAL_CODE].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessRepresentative][EFieldName.FIRST_NAME] = new Validator();
    validatorsRegistry[businessRepresentative][EFieldName.FIRST_NAME].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessRepresentative][EFieldName.FIRST_NAME].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 128 }),
      128,
    );

    validatorsRegistry[businessRepresentative][EFieldName.LAST_NAME] = new Validator();
    validatorsRegistry[businessRepresentative][EFieldName.LAST_NAME].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessRepresentative][EFieldName.LAST_NAME].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 128 }),
      128,
    );

    validatorsRegistry[businessRepresentative][EFieldName.JOB_TITLE] = new Validator();
    validatorsRegistry[businessRepresentative][EFieldName.JOB_TITLE].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessRepresentative][EFieldName.JOB_TITLE].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    // eslint-disable-next-line max-len,vue/max-len
    validatorsRegistry[businessRepresentative][EFieldName.IDENTIFICATION_NUMBER] = new Validator();
    validatorsRegistry[businessRepresentative][EFieldName.IDENTIFICATION_NUMBER].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessRepresentative][EFieldName.EMAIL] = new Validator();
    validatorsRegistry[businessRepresentative][EFieldName.EMAIL].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessRepresentative][EFieldName.EMAIL].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 180 }),
      180,
    );
    validatorsRegistry[businessRepresentative][EFieldName.EMAIL].addValidator(
      EValidatorType.EMAIL,
      validationMessagesLocalisation.invalidFormat,
    );

    validatorsRegistry[businessRepresentative][EFieldName.PHONE_NUMBER] = new Validator();
    validatorsRegistry[businessRepresentative][EFieldName.PHONE_NUMBER].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessRepresentative][EFieldName.PHONE_NUMBER].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 13 }),
      13,
    );
    validatorsRegistry[businessRepresentative][EFieldName.PHONE_NUMBER].addValidator(
      EValidatorType.REGEX,
      validationMessagesLocalisation.accountActivate.noventiq.phone.format,
      '^\\+[\\d-]+$',
    );

    validatorsRegistry[businessDetails][EFieldName.LEGAL_BUSINESS_NAME] = new Validator();
    validatorsRegistry[businessDetails][EFieldName.LEGAL_BUSINESS_NAME].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessDetails][EFieldName.LEGAL_BUSINESS_NAME].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 128 }),
      128,
    );

    validatorsRegistry[businessDetails][EFieldName.TAX_IDENTIFICATION_CODE] = new Validator();
    validatorsRegistry[businessDetails][EFieldName.TAX_IDENTIFICATION_CODE].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessDetails][EFieldName.TAX_IDENTIFICATION_CODE].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessDetails][EFieldName.DOING_BUSINESS_AS] = new Validator();
    validatorsRegistry[businessDetails][EFieldName.DOING_BUSINESS_AS].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessDetails][EFieldName.DOING_BUSINESS_AS].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[businessDetails][EFieldName.BUSINESS_WEBSITE] = new Validator();
    validatorsRegistry[businessDetails][EFieldName.BUSINESS_WEBSITE].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[businessDetails][EFieldName.BUSINESS_WEBSITE].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );
    validatorsRegistry[businessDetails][EFieldName.BUSINESS_WEBSITE].addValidator(
      EValidatorType.REGEX,
      validationMessagesLocalisation.invalidFormat,
      '^.+\\.+.+$',
    );

    validatorsRegistry[businessDetails][EFieldName.PRODUCT_DESCRIPTION] = new Validator();
    validatorsRegistry[businessDetails][EFieldName.PRODUCT_DESCRIPTION].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[supportDetails][EFieldName.PHONE_NUMBER] = new Validator();
    validatorsRegistry[supportDetails][EFieldName.PHONE_NUMBER].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 13 }),
      13,
    );
    validatorsRegistry[supportDetails][EFieldName.PHONE_NUMBER].addValidator(
      EValidatorType.REGEX,
      validationMessagesLocalisation.accountActivate.noventiq.phone.format,
      '^\\+[\\d-]+$',
    );

    validatorsRegistry[supportDetails][EFieldName.EMAIL] = new Validator();
    validatorsRegistry[supportDetails][EFieldName.EMAIL].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[supportDetails][EFieldName.EMAIL].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 180 }),
      180,
    );
    validatorsRegistry[supportDetails][EFieldName.EMAIL].addValidator(
      EValidatorType.EMAIL,
      validationMessagesLocalisation.invalidFormat,
    );

    validatorsRegistry[accountForPayouts][EFieldName.COUNTRY] = new Validator();
    validatorsRegistry[accountForPayouts][EFieldName.COUNTRY].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );

    validatorsRegistry[accountForPayouts][EFieldName.BANK_NAME] = new Validator();
    validatorsRegistry[accountForPayouts][EFieldName.BANK_NAME].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[accountForPayouts][EFieldName.BANK_NAME].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[accountForPayouts][EFieldName.IBAN] = new Validator();
    validatorsRegistry[accountForPayouts][EFieldName.IBAN].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[accountForPayouts][EFieldName.IBAN].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );

    validatorsRegistry[accountForPayouts][EFieldName.IBAN_CONFIRM] = new Validator();
    validatorsRegistry[accountForPayouts][EFieldName.IBAN_CONFIRM].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );
    validatorsRegistry[accountForPayouts][EFieldName.IBAN_CONFIRM].addValidator(
      EValidatorType.MAX_LENGTH,
      vueInstance.$t('validation_messages.max_length', { number: 255 }),
      255,
    );
    validatorsRegistry[accountForPayouts][EFieldName.IBAN_CONFIRM].addValidator(
      EValidatorType.CUSTOM,
      validationMessagesLocalisation.ibanDoesNotMatch,
      // TODO: Разобраться  с типами
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      (value, formData) => value === (formData as TFormData).accountForPayouts.iban,
    );

    validatorsRegistry[accountForPayouts][EFieldName.VAT_NUMBER] = new Validator();
    validatorsRegistry[accountForPayouts][EFieldName.VAT_NUMBER].addValidator(
      EValidatorType.NOT_EMPTY,
      validationMessagesLocalisation.required,
    );

    return validatorsRegistry;
  };

  validateField = (
    fieldConfig: IFormFieldItem<EFieldName, EFieldBlock>,
    formData: TFormData,
    formFieldStatusList: TFieldStatuses,
  ): TFieldStatuses => {
    const { fieldBlock, fieldName } = fieldConfig;

    let localFormFieldStatusList = cloneImmutable(formFieldStatusList);
    if (!localFormFieldStatusList[fieldBlock]) {
      localFormFieldStatusList[fieldBlock] = {} as { [key in EFieldName]: IFieldStatus };
    }

    if (!fieldConfig.validator) {
      localFormFieldStatusList[fieldBlock][fieldName] = {
        code: EValidationFieldStatusCode.DEFAULT,
      };
    } else if (
      fieldConfig.conditionsList
      && !checkCondition.resolveAndCondition(fieldConfig.conditionsList, formData)
    ) {
      localFormFieldStatusList[fieldBlock][fieldName] = {
        code: EValidationFieldStatusCode.DEFAULT,
      };
    } else {
      fieldConfig.validator.clear();

      fieldConfig.validator.validate(formData[fieldBlock][fieldName], formData);

      localFormFieldStatusList = this.fieldValidatorHandler.changeStatus(
        fieldConfig,
        fieldConfig.validator,
        localFormFieldStatusList,
      );
    }

    return localFormFieldStatusList;
  };

  validateTab = (formConfig: IFormConfig, formData: TFormData) => {
    if (formConfig.id === EPageTab.SUMMARY) {
      return;
    }

    const utilsService = this.serviceContainer.utilsService;

    let localFormFieldStatusList = utilsService.data.cloneImmutable(this.state.formFieldStatusList);
    formConfig.formConfig.forEach((block) => {
      block.items.forEach((field) => {
        if (!localFormFieldStatusList[field.fieldBlock]) {
          localFormFieldStatusList[field.fieldBlock] = {} as { [key in EFieldName]: IFieldStatus };
        }
        localFormFieldStatusList[field.fieldBlock][field.fieldName] = {
          code: EValidationFieldStatusCode.DEFAULT,
        };
      });
      block.items.filter(
        (field) => !field.conditionsList
            || (field.conditionsList && checkCondition.resolveAndCondition(field.conditionsList, formData)),
      ).forEach((field) => {
        localFormFieldStatusList = this.validateField(field, formData, localFormFieldStatusList);
      });
    });

    this.state.formFieldStatusList = localFormFieldStatusList;

    let status = ETabStatus.SUCCESS;
    formConfig.formConfig.forEach((block) => {
      block.items.forEach((field) => {
        status = this.tabValidatorHandler.changeStatus(
          field,
          this.state.formFieldStatusList,
          status,
        );
      });
    });

    this.state.tabs = this.state.tabs.map((tab) => {
      if (tab.id === formConfig.id) {
        tab.status = status;
      }

      return tab;
    });
  };
}

export default ValidatorHandler;
